import { ITheme, IStyle, mergeStyleSets, getTheme } from '@fluentui/react';
import React, { useEffect, useRef, useState } from 'react';
import { SteppedListSection, StepSectionState } from '../../../components/step-section';
import { AddressElement, useElements } from '@stripe/react-stripe-js';
import { StripeAddressElementChangeEvent } from '@stripe/stripe-js';
import { BwjButton, BwjButtonType } from '../../../../../framework/components/bwj-button';

export interface IStripeContact {
  name: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  address: IStripeAddress;
}

export interface IStripeAddress {
  line1: string;
  line2: string | null;
  city: string;
  state: string;
  country: string;
  postal_code: string;
}

interface IPaymentSectionBillingAddressProps {
  state: StepSectionState;
  onEditSection: () => void;
  onAdvanceToNextSection: (address: IStripeContact) => void;
  onFormChanged: () => void;
}

const getStyleClasses = (theme: ITheme) => {
  const buttonContainer: IStyle = {
    marginTop: '1rem',

    '& button': {
      width: '100%',
    }
  };

  return mergeStyleSets({
    buttonContainer,
  });
}

export const PaymentSectionBillingAddress: React.FunctionComponent<IPaymentSectionBillingAddressProps> = (props) => {
  const theme = getTheme();
  const cs = getStyleClasses(theme);
  const elements = useElements();
  const [formComplete, setFormComplete] = useState(false);
  const [formInvalid, setFormInvalid] = useState(true);
  const changeReported = useRef(false);
  const { state } = props;
  useEffect(() => {
    if(state === StepSectionState.Active) {
      changeReported.current = false;
    }
  }, [state]);

  const onAddressChange = (event: StripeAddressElementChangeEvent) => {
    setFormComplete(event.complete);
    setFormInvalid(false);

    // we only need to report this once after this section becomes active, because
    // once reported, user can't move until they hit continue
    if(changeReported.current) { return; }
    changeReported.current = true;
    props.onFormChanged();
  }

  const onAdvanceToNextSection = () => {
    if(!elements) { return; }
    const address = elements.getElement('address');
    if(!address) { return; }

    address.getValue()
    .then(result => {
      if(result.complete) {
        props.onAdvanceToNextSection(result.value);
      } else {
        setFormInvalid(true);
      }
    });
  };

  return (
    <SteppedListSection
      id={'stnBillAddress'}
      title={'Provide A Billing Address'}
      state={props.state}
      formBusy={false}
      onEditSection={props.onEditSection}
    >
      <AddressElement
        options={{ mode: 'billing' }}
        onChange={onAddressChange}
      />
      <div className={cs.buttonContainer}>
        <BwjButton
          buttonStyle={BwjButtonType.PrimaryStandard}
          text={'Continue'}
          disabled={formComplete === false || formInvalid}
          onClick={onAdvanceToNextSection}
        />
      </div>
    </SteppedListSection>
  );
};
